import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Music, Star, Sparkles, Zap, Image, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { useAuth } from "@/hooks/useAuth";
import SEO from "@/components/SEO";
import { productSchemas, createBreadcrumbSchema } from "@/lib/jsonLd";

const pricingPlans = [
  {
    name: "Essentials Plan",
    icon: Music,
    price: 35,
    description: "Perfect for birthdays, graduations, and everyday meaningful moments.",
    features: [
      "One personalized song created from your story",
      "Custom lyrics and music",
      
      "Digital MP3 delivery",
      "Languages: English, Spanish, or bilingual lyrics",
    ],
    popular: false,
  },
  {
    name: "Premium Song + Slideshow",
    icon: Image,
    price: 85,
    description: "Perfect for proposals, quinceañeras, anniversaries, memorials, and milestone moments.",
    features: [
      "One personalized song",
      
      "Slideshow video created with 15–40 photos you upload",
      "Professionally paced visuals matched to the music",
      "Digital MP4 video delivery",
      "Digital lyric sheet",
      "Everything in Essentials, plus:",
    ],
    popular: true,
  },
];

const howItWorks = [
  {
    step: 1,
    title: "Choose Your Package",
    description: "Select Basic Song or Premium Song + Slideshow.",
  },
  {
    step: 2,
    title: "Share Your Story",
    description: "Answer a few guided questions to help us capture the moment.",
  },
  {
    step: 3,
    title: "Upload Photos (Premium only)",
    description: "Add 15–40 photos to be included in your slideshow video.",
  },
  {
    step: 4,
    title: "We Create Your Song",
    description: "Your personalized song is crafted based on the details you provide.",
  },
  {
    step: 5,
    title: "Receive & Share",
    description: "Download your song or video and enjoy your moment anytime.",
  },
];

const Pricing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleGetStarted = (planType: "essentials" | "premium") => {
    if (user) {
      // User is logged in, go directly to survey
      navigate(`/create?plan=${planType}`);
    } else {
      // User needs to log in first, store return URL
      sessionStorage.setItem("authReturnUrl", `/create?plan=${planType}`);
      navigate("/login");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Pricing — Custom Song Packages"
        description="Simple, transparent pricing for custom songs. Essentials package at $35, Premium Song + Slideshow at $85. Personalized music gifts delivered in 48–72 hours."
        canonical="https://yourmomentsong.com/pricing"
        url="https://yourmomentsong.com/pricing"
        jsonLd={[
          ...productSchemas,
          createBreadcrumbSchema([
            { name: "Home", url: "https://yourmomentsong.com/" },
            { name: "Pricing", url: "https://yourmomentsong.com/pricing" },
          ]),
        ]}
      />
      <Header />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto mb-12 md:mb-16"
          >
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
              <Sparkles className="w-4 h-4" aria-hidden="true" />
              <span className="text-sm font-medium">Simple, Transparent Pricing</span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">
              Choose Your Perfect Package
            </h1>
            <p className="text-lg text-muted-foreground">
              Every package includes a professionally composed, original song tailored 
              to your unique story and memories.
            </p>
          </motion.div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto mb-12">
            {pricingPlans.map((plan, index) => {
              const IconComponent = plan.icon;
              return (
                <motion.div
                  key={plan.name}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative bg-card rounded-2xl p-6 md:p-8 shadow-soft ${
                    plan.popular ? "ring-2 ring-primary shadow-glow" : ""
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                      <Star className="w-4 h-4 fill-current" aria-hidden="true" />
                      Most Popular
                    </div>
                  )}

                  <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full mb-4">
                      <IconComponent className="w-6 h-6 text-primary" aria-hidden="true" />
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                      {plan.name}
                    </h3>
                    <div className="flex items-baseline justify-center gap-1 mb-3">
                      <span className="text-4xl font-bold text-foreground">${plan.price}</span>
                    </div>
                    <p className="text-muted-foreground text-sm">
                      {plan.description}
                    </p>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                        <span className="text-foreground text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    variant={plan.popular ? "hero" : "outline"}
                    size="lg"
                    className="w-full"
                    onClick={() => handleGetStarted(plan.name === "Premium Song + Slideshow" ? "premium" : "essentials")}
                  >
                    <Music className="w-5 h-5 mr-2" aria-hidden="true" />
                    Get Started
                  </Button>
                </motion.div>
              );
            })}
          </div>

          {/* Rush Delivery Add-on */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="max-w-4xl mx-auto mb-16"
          >
            <div className="bg-accent/20 border border-accent rounded-2xl p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-accent/30 rounded-full shrink-0">
                    <Zap className="w-6 h-6 text-accent-foreground" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-semibold text-foreground mb-1">
                      ⚡ Rush Delivery
                    </h3>
                    <p className="text-muted-foreground text-sm mb-2">
                      Guaranteed delivery within 24 hours
                    </p>
                    <p className="text-muted-foreground text-sm">
                      Available for both packages
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-3xl font-bold text-foreground">+$10</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Standard Delivery Note */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="text-center max-w-2xl mx-auto mb-16"
          >
            <p className="text-muted-foreground">
              <strong className="text-foreground">Standard delivery:</strong> 48-72 hours
            </p>
          </motion.div>

          {/* How It Works */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground text-center mb-10">
              How It Works
            </h2>
            <div className="space-y-6">
              {howItWorks.map((item, index) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div className="flex items-center justify-center w-10 h-10 bg-primary text-primary-foreground rounded-full font-bold shrink-0">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-foreground mb-1">
                      {item.title}
                    </h3>
                    <p className="text-muted-foreground">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Delivery & Support */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto mb-16"
          >
            <div className="bg-card border border-border rounded-2xl p-6 md:p-8">
              <h3 className="font-heading text-xl font-semibold text-foreground mb-4 text-center">
                Delivery & Support
              </h3>
              <ul className="space-y-3 max-w-xl mx-auto">
                <li className="flex items-start gap-3">
                   <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                   <span className="text-foreground text-sm">Orders are delivered digitally</span>
                 </li>
                 <li className="flex items-start gap-3">
                   <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                   <span className="text-foreground text-sm">Rush delivery ensures completion within 24 hours</span>
                 </li>
                 <li className="flex items-start gap-3">
                   <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                   <span className="text-foreground text-sm">All photos are private and used only for your video</span>
                 </li>
                 <li className="flex items-start gap-3">
                   <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                  <span className="text-foreground text-sm">Completed songs remain available for download in your account</span>
                </li>
              </ul>
            </div>
          </motion.div>




          {/* Final CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-6">
              Ready to Turn Your Moment Into a Song?
            </h2>
            <p className="text-muted-foreground mb-2">Create something meaningful.</p>
            <p className="text-muted-foreground mb-2">Create something personal.</p>
            <p className="text-muted-foreground mb-6">Create your moment.</p>
            <Button 
              variant="hero" 
              size="lg" 
              onClick={() => navigate("/pricing")}
            >
              <Music className="w-5 h-5 mr-2" aria-hidden="true" />
              Choose Your Package
            </Button>
          </motion.div>

          {/* FAQ Teaser */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-16 md:mt-24"
          >
            <p className="text-muted-foreground mb-4">
              Have questions? Check out our frequently asked questions.
            </p>
            <Button variant="outline" asChild>
              <Link to="/faq">View FAQ</Link>
            </Button>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Pricing;
